import React, {useState, useEffect} from 'react';
import { Box, Chip, Stack, Typography, Link } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider, TextField } from 'components/Form';
import { useAuth } from 'lib/auth';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { RecentBlogs } from './RecentBlogs';
import { RecentJobAds } from './RecentJobAds';

const detailsTabName = 'details';

const getDefaultValues = () => ({
  aboutUs: '',
  website: '',
});

export const DetailsTab = ({ currentTab, companyProfile }) => {
  const { t } = useTranslation();
  const { account } = useAuth();
  const [ loading, setLoading ] = useState(true);

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (companyProfile) {
      const {
        about_us,
        website,
      } = companyProfile;

      methods.reset({
        aboutUs: about_us,
        website: website,
      });
    }

    setLoading(false);
  }, [companyProfile])  

  const onSubmit = () => {};

  if (currentTab !== detailsTabName) return null;

  if (loading) {
    return null;
  }

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction={{xs: "column", md: "row"}} bgcolor={'background.paper'} gap={2}>
        <Stack flex={2} direction="column" gap={2}>
          <Box
            sx={{
              p: 3,
              border: '1px solid',
              borderRadius: '8px',
              backgroundColor: 'background.paper',
              borderColor: 'grey.100'
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              {t('website:publicProfile.details.aboutUs')}
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, whiteSpace: 'pre-wrap', fontWeight: 500, lineHeight: 1.5 }}>
              {companyProfile?.about_us || t('website:publicProfile.details.noDescription')}
            </Typography>

            {companyProfile?.website?.length > 0 && (
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', lineHeight: 1.5  }}>
                  {t('website:publicProfile.details.website')}
                </Typography>
                {companyProfile?.website ? (
                  <Link
                    href={companyProfile?.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body1"
                  >
                    {companyProfile?.website}
                  </Link>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500, lineHeight: 1.5 }}>
                    {t('website:publicProfile.details.notProvided')}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {companyProfile?.competence_areas?.length > 0 && (
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                borderColor: 'grey.100'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t('website:publicProfile.details.competenceAreas')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {companyProfile.competence_areas.map((area) => (
                  <Chip key={area.id} label={area.name} sx={{ fontSize: '0.875rem', fontWeight: 500 }} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
        <Stack flex={{md: 1.25, lg: 1}} direction="column" gap={1}>
          <RecentBlogs slug={companyProfile?.slug} />
          <RecentJobAds slug={companyProfile?.slug} />
        </Stack>
      </Stack>
      </LocalizationProvider>
    </FormProvider>
  );
};
